import React, { useState } from 'react';
import { toast } from 'sonner';
import { Eye, Download, Copy, Trash, Spinner } from '@phosphor-icons/react';
import { creationsAPI } from '@/utils/api';
import api from '@/utils/api';

/**
 * Round 61 — Card-level deliverable actions.
 *
 * Renders 4 mobile-friendly icon buttons inline on every My Projects
 * card: View · Download · Copy · Delete. Replaces the legacy
 * "Open Full · Download PDF / HTML" combined button.
 *
 * Buttons hit-target is 40×40px (≥ Apple guideline of 44×44 effective
 * tap area with surrounding padding). Each fires a separate action so
 * mobile users can tap them confidently.
 *
 * Props
 * -----
 * creationId   string  — required
 * title        string  — used in confirm + download filename hints
 * type         string  — asset type, used to choose download format
 * copyText     string  — pre-computed plain text for the Copy action
 * downloadable bool    — when false, the Download button is hidden
 *                        (e.g. starter tier, video-only, business_package)
 * onView       () => void   — opens the modal in preview mode
 * onDeleted    (id) => void — fires after a successful DELETE
 */
const DeliverableActions = ({
  creationId,
  title,
  type,
  copyText,
  downloadable = true,
  onView,
  onDeleted,
}) => {
  const [deleting, setDeleting] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // Pick the format we expect — PDFs for most types; HTML for sales
  // pages and lead magnets (the styled HTML IS the deliverable).
  const preferredFormat =
    type === 'sales_page' || type === 'lead_magnet' || type === 'ebook'
      ? 'pdf'
      : 'pdf';

  const handleView = (e) => {
    e?.stopPropagation?.();
    if (onView) {
      onView(creationId);
    } else {
      // Fallback — open the CreationDetailModal via custom event.
      window.dispatchEvent(
        new CustomEvent('paygbot:open-creation', { detail: creationId }),
      );
    }
  };

  const handleDownload = async (e) => {
    /* Phase G — V2 grants export to every subscriber */
    e?.stopPropagation?.();
    if (downloading) return;
    setDownloading(true);
    try {
      const res = await api.get(
        `/creations/${creationId}/download?format=${preferredFormat}`,
        { responseType: 'blob' },
      );
      const blob = new Blob([res.data], { type: res.headers['content-type'] });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const cd = res.headers['content-disposition'] || '';
      const match = /filename="?([^"]+)"?/.exec(cd);
      a.download = match
        ? match[1]
        : `${title || 'asset'}.${preferredFormat}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Download started.');
    } catch (err) {
      const status = err?.response?.status;
      if (status === 402) {
        toast.error(
          err?.response?.data?.detail ||
            'Downloads require Growth or Pro. Upgrade to access exports.',
        );
      } else if (status === 404) {
        toast.error('This asset has no downloadable file yet.');
      } else {
        console.error('download failed:', err);
        toast.error('Download failed.');
      }
    } finally {
      setDownloading(false);
    }
  };

  const handleCopy = async (e) => {
    e?.stopPropagation?.();
    const text = (copyText && copyText.trim()) || title || '';
    if (!text) {
      toast.info('Nothing to copy.');
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied to clipboard.');
    } catch {
      toast.error('Copy failed — please select manually.');
    }
  };

  const handleDelete = async (e) => {
    e?.stopPropagation?.();
    const ok = window.confirm(
      `Delete "${title || 'this asset'}"? This permanently removes it from My Projects and cannot be undone.`,
    );
    if (!ok) return;
    setDeleting(true);
    try {
      await creationsAPI.delete(creationId);
      toast.success('Asset deleted.');
      onDeleted?.(creationId);
    } catch (err) {
      console.error('delete failed:', err);
      toast.error(err?.response?.data?.detail || 'Could not delete the asset.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div
      className="flex items-center gap-2 flex-wrap"
      data-testid={`deliverable-actions-${creationId}`}
      onClick={(e) => e.stopPropagation()}
    >
      <ActionBtn onClick={handleView} label="View" testid={`deliverable-view-${creationId}`}>
        <Eye size={18} />
      </ActionBtn>
      {downloadable && (
        <ActionBtn
          onClick={handleDownload}
          label="Download"
          disabled={downloading}
          testid={`deliverable-download-${creationId}`}
        >
          {downloading ? <Spinner size={18} className="animate-spin" /> : <Download size={18} />}
        </ActionBtn>
      )}
      <ActionBtn onClick={handleCopy} label="Copy" testid={`deliverable-copy-${creationId}`}>
        <Copy size={18} />
      </ActionBtn>
      <ActionBtn
        onClick={handleDelete}
        label="Delete"
        disabled={deleting}
        danger
        testid={`deliverable-delete-${creationId}`}
      >
        {deleting ? <Spinner size={18} className="animate-spin" /> : <Trash size={18} />}
      </ActionBtn>
    </div>
  );
};

// Round 61 — hoisted out of DeliverableActions to satisfy
// react/no-unstable-nested-components.
const ActionBtn = ({ onClick, label, children, disabled, testid, danger }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    data-testid={testid}
    aria-label={label}
    title={label}
    className={
      'inline-flex items-center justify-center w-11 h-11 sm:w-10 sm:h-10 rounded-lg border ' +
      (danger
        ? 'border-rose-500/30 bg-rose-500/10 text-rose-200 hover:bg-rose-500/20'
        : 'border-white/15 bg-white/5 text-white hover:bg-white/10') +
      ' transition-colors disabled:opacity-50'
    }
  >
    {children}
  </button>
);

export default DeliverableActions;
